import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import RentalDashboard from './dashboard/RentalDashboard';
import RentalInventory from './inventory/RentalInventory';
import RentalContractList from './contracts/RentalContractList';
import NewRentalForm from './NewRentalForm';
import { rentalApi } from '../../../api/rentalApi';
import './RentalTool.css';

/**
 * Main Rental Tool Component.
 * Orchestrates the dashboard, inventory management, and rental creation wizard.
 * 
 * @returns {JSX.Element} The rendered rental tool.
 */
export default function RentalTool() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [showNewRental, setShowNewRental] = useState(false);
    const [rentals, setRentals] = useState([]);
    const [stats, setStats] = useState({ active: 0, pending: 0, total: 0 });
    const [showContractEditor, setShowContractEditor] = useState(false);
    const [contractData, setContractData] = useState({
        contract_text: '',
        conditions: '',
        other_terms: '',
        contact_data: '',
        emergency_contact: '',
        emergency_behavior: ''
    });
    const [isSavingContract, setIsSavingContract] = useState(false);

    const fetchContractText = async () => {
        try {
            const data = await rentalApi.getContract();
            setContractData(data.contract_data || {
                contract_text: '',
                conditions: '',
                other_terms: '',
                contact_data: '',
                emergency_contact: '',
                emergency_behavior: ''
            });
        } catch (e) {
            console.error('Error fetching contract', e);
        }
    };

    const handleSaveContract = async () => {
        setIsSavingContract(true);
        try {
            await rentalApi.saveContract(contractData);
            setShowContractEditor(false);
        } catch (e) {
            console.error(e);
            alert('Fehler beim Speichern');
        } finally {
            setIsSavingContract(false);
        }
    };

    const fetchRentals = async () => {
        try {
            const data = await rentalApi.getRentals();
            setRentals(data);
            const active = data.filter(r => r.status === 'active').length;
            const now = new Date();
            const pending = data.filter(r => r.status === 'active' && new Date(r.end_date) < now).length;
            setStats({ active, pending, total: data.length });
        } catch (err) {
            console.error('FETCH_RENTALS_ERROR:', err);
        }
    };

    useEffect(() => {
        fetchRentals();
        fetchContractText();
    }, []);

    const renderContent = () => {
        if (showNewRental) return <NewRentalForm onCancel={() => setShowNewRental(false)} onSuccess={() => { setShowNewRental(false); fetchRentals(); setActiveTab('contracts'); }} />;
        
        switch (activeTab) {
            case 'overview': return <RentalDashboard rentals={rentals} onRefresh={fetchRentals} navigateToTab={setActiveTab} />;
            case 'vehicles': return <RentalInventory activeSubTab="vehicle" onTabChange={(cat) => setActiveTab(cat + 's')} globalContract={contractData} />;
            case 'materials': return <RentalInventory activeSubTab="material" onTabChange={(cat) => setActiveTab(cat + 's')} globalContract={contractData} />;
            case 'other': return <RentalInventory activeSubTab="other" onTabChange={(cat) => setActiveTab(cat + 's')} globalContract={contractData} />;
            case 'contracts': return <RentalContractList />;
            default: return <RentalDashboard rentals={rentals} onRefresh={fetchRentals} navigateToTab={setActiveTab} />;
        }
    };

    const handleBack = () => {
        navigate('/tools');
    };

    return (
        <div className="app rental-app-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <header className="app-header">
                <div className="header-left">
                    <button className="back-button" onClick={handleBack}>← Zurück</button>
                    <h1>Rental Control</h1>
                </div>
                <div className="user-info">
                    <span>{user?.username} ({user?.role})</span>
                    <button onClick={logout}>Logout</button>
                </div>
            </header>

            <main className="rental-content-area" key={activeTab + (showNewRental ? '-new' : '')} style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', padding: '20px', display: 'flex', flexDirection: 'column', animation: 'fadeInScale 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                {!showNewRental && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div className="admin-nav-tabs" style={{ display: 'flex', gap: '8px' }}>
                            <button className={`nav-tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'overview' ? '#4f46e5' : '#e5e7eb', color: activeTab === 'overview' ? 'white' : '#4b5563', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>📊 Dashboard</button>
                            <button className={`nav-tab-btn ${activeTab === 'vehicles' ? 'active' : ''}`} onClick={() => setActiveTab('vehicles')} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'vehicles' ? '#4f46e5' : '#e5e7eb', color: activeTab === 'vehicles' ? 'white' : '#4b5563', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>🚜 Fahrzeuge</button>
                            <button className={`nav-tab-btn ${activeTab === 'materials' ? 'active' : ''}`} onClick={() => setActiveTab('materials')} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'materials' ? '#4f46e5' : '#e5e7eb', color: activeTab === 'materials' ? 'white' : '#4b5563', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>📦 Material</button>
                            <button className={`nav-tab-btn ${activeTab === 'other' ? 'active' : ''}`} onClick={() => setActiveTab('other')} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'other' ? '#4f46e5' : '#e5e7eb', color: activeTab === 'other' ? 'white' : '#4b5563', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>🛠️ Sonstiges</button>
                            <button className={`nav-tab-btn ${activeTab === 'contracts' ? 'active' : ''}`} onClick={() => setActiveTab('contracts')} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: activeTab === 'contracts' ? '#4f46e5' : '#e5e7eb', color: activeTab === 'contracts' ? 'white' : '#4b5563', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>📄 Dokumentation</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {activeTab !== 'overview' && (
                                <button className="rental-btn-secondary" style={{ padding: '10px 20px', fontSize: '0.85rem', border: '1px solid var(--p-slate-200)', background: '#fff', borderRadius: '8px', cursor: 'pointer' }} onClick={() => setShowContractEditor(true)}>
                                    📝 MIETVERTRAG BEARBEITEN
                                </button>
                            )}
                            <button className="rental-btn-primary" style={{ background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', fontWeight: '600', cursor: 'pointer' }} onClick={() => setShowNewRental(true)}>
                                + NEU ANLEGEN
                            </button>
                        </div>
                    </div>
                )}
                {renderContent()}
            </main>

            {/* CONTRACT EDITOR MODAL */}
            {showContractEditor && (
                <div className="rental-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }}>
                    <div className="rental-modal-content premium-card" style={{ width: '800px', maxWidth: '90%', background: '#fff', borderRadius: '16px', padding: '30px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--p-slate-400)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '4px' }}>Globale Vorlage</div>
                                <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--p-slate-900)', fontWeight: '900' }}>MIETVERTRAG BEARBEITEN</h2>
                            </div>
                            <button onClick={() => setShowContractEditor(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--p-slate-400)' }}>✕</button>
                        </div>
                        <div style={{ marginBottom: '20px', fontSize: '0.85rem', color: 'var(--p-slate-500)', lineHeight: '1.5', background: 'var(--p-slate-50)', padding: '12px', borderRadius: '8px' }}>
                            Definieren Sie hier Ihre allgemeinen Mietbedingungen oder Verträge. Dieser Text wird künftig automatisch bei jeder neuen Vermietung als Anhang an das PDF-Protokoll angehängt. Formatierungen bleiben als reiner Text mit Zeilenumbrüchen erhalten.
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div className="form-group-compact">
                                <label style={{ fontSize: '0.7rem', fontWeight: '800' }}>ALLGEMEINE MIETBEDINGUNGEN / VERTRAG</label>
                                <textarea value={contractData.contract_text} onChange={e => setContractData({...contractData, contract_text: e.target.value})} style={{ width: '100%', minHeight: '150px', padding: '12px', borderRadius: '8px', border: '1px solid var(--p-slate-200)' }} />
                            </div>
                            <div className="form-group-compact">
                                <label style={{ fontSize: '0.7rem', fontWeight: '800' }}>KONDITIONEN</label>
                                <textarea value={contractData.conditions} onChange={e => setContractData({...contractData, conditions: e.target.value})} style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '8px', border: '1px solid var(--p-slate-200)' }} />
                            </div>
                            <div className="form-group-compact">
                                <label style={{ fontSize: '0.7rem', fontWeight: '800' }}>SONSTIGE BEDINGUNGEN</label>
                                <textarea value={contractData.other_terms} onChange={e => setContractData({...contractData, other_terms: e.target.value})} style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '8px', border: '1px solid var(--p-slate-200)' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div className="form-group-compact">
                                    <label style={{ fontSize: '0.7rem', fontWeight: '800' }}>KONTAKTDATEN (ZENTRALE)</label>
                                    <textarea value={contractData.contact_data} onChange={e => setContractData({...contractData, contact_data: e.target.value})} style={{ width: '100%', minHeight: '80px', padding: '12px', borderRadius: '8px', border: '1px solid var(--p-slate-200)' }} />
                                </div>
                                <div className="form-group-compact">
                                    <label style={{ fontSize: '0.7rem', fontWeight: '800' }}>NOTFALLKONTAKT</label>
                                    <textarea value={contractData.emergency_contact} onChange={e => setContractData({...contractData, emergency_contact: e.target.value})} style={{ width: '100%', minHeight: '80px', padding: '12px', borderRadius: '8px', border: '1px solid var(--p-slate-200)' }} />
                                </div>
                            </div>
                            <div className="form-group-compact">
                                <label style={{ fontSize: '0.7rem', fontWeight: '800', color: 'var(--optimox-blue)' }}>VERHALTEN IM NOTFALL</label>
                                <textarea value={contractData.emergency_behavior} onChange={e => setContractData({...contractData, emergency_behavior: e.target.value})} style={{ width: '100%', minHeight: '80px', padding: '12px', borderRadius: '8px', border: '1px solid var(--optimox-blue)' }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                            <button className="rental-btn-secondary" style={{ padding: '12px 24px' }} onClick={() => setShowContractEditor(false)}>ABBRECHEN</button>
                            <button className="rental-btn-primary" style={{ padding: '12px 24px' }} onClick={handleSaveContract} disabled={isSavingContract}>
                                {isSavingContract ? 'SPEICHERT...' : 'ÄNDERUNGEN SPEICHERN'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
